import { Injectable, OnDestroy } from '@angular/core';

import { ConfigService } from '../services/config.service';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { NotificacionHistorialService } from '../services/notificacion-historial.service';
import { TokenService } from '../services/token.service';

type SocketState = 'disconnected' | 'connecting' | 'connected';
type EchoChannel = {
  listen: (eventName: string, callback: (payload: unknown) => void) => EchoChannel;
};
type EchoConnection = {
  bind: (eventName: string, callback: () => void) => void;
  unbind_all: () => void;
};
type EchoClient = {
  connector: {
    pusher: {
      connection: EchoConnection;
    };
  };
  channel: (channelName: string) => EchoChannel;
  leave: (channelName: string) => void;
  disconnect: () => void;
};
type EchoPusherOptions = {
  broadcaster: 'pusher';
  key: string;
  cluster?: string;
  forceTLS: boolean;
  wsHost: string;
  wsPort?: number;
  wssPort?: number;
  enabledTransports: ('ws' | 'wss')[];
  authEndpoint?: string;
  auth: {
    headers: Record<string, string>;
  };
};

@Injectable({ providedIn: 'root' })
export class SocketConnectionService implements OnDestroy {
  private static readonly RECONNECT_MIN_DELAY_MS = 1000;
  private static readonly RECONNECT_MAX_DELAY_MS = 30000;

  private echo: EchoClient | null = null;
  private state: SocketState = 'disconnected';
  private connectionToken: string | null = null;
  private subscribedChannelName: string | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private manualDisconnect = false;

  constructor(
    private readonly configService: ConfigService,
    private readonly tokenService: TokenService,
    private readonly authService: AuthService,
    private readonly notificationService: NotificationService,
    private readonly notificacionHistorialService: NotificacionHistorialService,
  ) {}

  isConnected(): boolean {
    return this.state === 'connected';
  }

  InicializarConnection(): void {
    if (this.state === 'connecting') {
      return;
    }

    this.manualDisconnect = false;
    const token = this.tokenService.getAccessToken();
    if (!token) {
      this.desconectarConnection();
      return;
    }

    if (this.isConnected() && this.connectionToken === token) {
      return;
    }

    const options = this.resolveConnectionOptions(token);
    if (!options) {
      return;
    }

    this.desconectarConnection(false);
    this.state = 'connecting';
    this.connectionToken = token;
    void this.initializeEchoClient(options, token);
  }

  private async initializeEchoClient(options: EchoPusherOptions, token: string): Promise<void> {
    const [{ default: Echo }, { default: Pusher }] = await Promise.all([
      import('laravel-echo'),
      import('pusher-js'),
    ]);

    if (this.connectionToken !== token || this.state !== 'connecting') {
      return;
    }

    (window as Window & { Pusher?: unknown }).Pusher = Pusher;

    const echo = new Echo(options) as unknown as EchoClient;
    this.echo = echo;
    this.subscribeToUserNotifications(echo);

    const pusherConnection = echo.connector.pusher.connection;
    pusherConnection.bind('connected', () => {
      if (this.echo !== echo) {
        echo.disconnect();
        return;
      }

      this.state = 'connected';
      this.reconnectAttempts = 0;
      this.clearReconnectTimer();
    });
    pusherConnection.bind('disconnected', () => {
      if (this.echo !== echo) {
        return;
      }

      this.state = 'disconnected';
      this.scheduleReconnect();
    });
    pusherConnection.bind('error', () => {
      if (this.echo !== echo) {
        return;
      }

      this.state = 'disconnected';
      this.scheduleReconnect();
    });
  }

  desconectarConnection(manual = true): void {
    this.manualDisconnect = manual;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;

    if (!this.echo) {
      this.state = 'disconnected';
      this.connectionToken = null;
      return;
    }

    const activeEcho = this.echo;
    this.clearActiveConnection(activeEcho);
    activeEcho.disconnect();
  }

  ngOnDestroy(): void {
    this.desconectarConnection();
  }

  private clearActiveConnection(echo: EchoClient): void {
    if (this.echo === echo) {
      if (this.subscribedChannelName) {
        echo.leave(this.subscribedChannelName);
        this.subscribedChannelName = null;
      }
      echo.connector.pusher.connection.unbind_all();
      this.echo = null;
      this.state = 'disconnected';
      this.connectionToken = null;
    }
  }

  private subscribeToUserNotifications(echo: EchoClient): void {
    const user = this.authService.getCurrentUser();
    const userId = this.normalizeUserId(user?.id);
    if (!userId) {
      return;
    }

    const channelName = `US${userId}`;
    this.subscribedChannelName = channelName;
    echo.channel(channelName).listen('.RealtimeNotification', (payload: unknown) => {
      this.notificationService.info(this.resolveRealtimeMessage(payload));
      this.notificacionHistorialService.notifyRefreshListado();
    });
  }

  private normalizeUserId(userId: string | number | undefined): string | null {
    if (typeof userId === 'number' && Number.isFinite(userId)) {
      return String(userId);
    }

    if (typeof userId === 'string') {
      const trimmed = userId.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    return null;
  }

  private resolveConnectionOptions(token: string): EchoPusherOptions | null {
    const echoConfig = {
      app_key: this.configService.getAppConfig().app_notif_key,
      host: this.configService.getAppConfig().host_notif,
      cluster: this.configService.getAppConfig().cluster_notif,
      port: this.configService.getAppConfig().port_notif,
      auth_notif_endpoint: this.configService.getAppConfig().auth_notif_endpoint,
    };
    if (!echoConfig) {
      return null;
    }

    const key = echoConfig.app_key?.trim();
    const host = echoConfig.host?.trim();
    if (!key || !host) {
      return null;
    }

    let wsHost = host;
    let parsedPort = Number.isFinite(echoConfig.port) ? Number(echoConfig.port) : undefined;
    let isTls = parsedPort === 443;
    if (/^(wss?|https?):\/\//i.test(host)) {
      try {
        const parsedHost = new URL(host);
        wsHost = parsedHost.hostname;
        if (!parsedPort && parsedHost.port) {
          parsedPort = Number(parsedHost.port);
        }
        isTls = parsedHost.protocol === 'wss:' || parsedHost.protocol === 'https:';
      } catch {
        return null;
      }
    }

    return {
      broadcaster: 'pusher',
      key,
      ...(echoConfig.cluster ? { cluster: echoConfig.cluster } : {}),
      forceTLS: isTls,
      wsHost,
      wsPort: parsedPort,
      wssPort: parsedPort,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: echoConfig.auth_notif_endpoint,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    };
  }

  private resolveRealtimeMessage(payload: unknown): string {
    if (!payload || typeof payload !== 'object') {
      return 'Nueva notificacion recibida.';
    }

    const eventPayload = payload as Record<string, unknown>;
    const directMessage = eventPayload['message'];
    if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
      return directMessage.trim();
    }

    const nestedData = eventPayload['data'];
    if (nestedData && typeof nestedData === 'object') {
      const dataMessage = (nestedData as Record<string, unknown>)['message'];
      if (typeof dataMessage === 'string' && dataMessage.trim().length > 0) {
        return dataMessage.trim();
      }
    }

    return 'Nueva notificacion recibida.';
  }

  private scheduleReconnect(): void {
    if (this.manualDisconnect || this.reconnectTimer) {
      return;
    }

    const token = this.tokenService.getAccessToken();
    if (!token) {
      return;
    }

    const expDelay = SocketConnectionService.RECONNECT_MIN_DELAY_MS * 2 ** this.reconnectAttempts;
    const delayMs = Math.min(expDelay, SocketConnectionService.RECONNECT_MAX_DELAY_MS);
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.state !== 'disconnected') {
        return;
      }
      this.InicializarConnection();
    }, delayMs);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}
